import React from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';

import tosData from './termsOfService.json';

/**
 * Terms of Service Modal component
 *
 * Displays the full Terms of Service content in a modal dialog when the user
 * clicks the TOS link. Implements requirements in docs/REQUIREMENTS.md section 5.1.3.
 *
 * @component
 * @see /docs/REQUIREMENTS.md section 5.1.0
 */
const TermsOfServiceModal = (props) => {
  const { open, onClose, classes } = props;
  const paperRef = React.useRef(null);

  // Focus the role=dialog Paper (not MUI's outer wrapper) so its name is announced.
  const handleEntered = () => {
    paperRef.current?.focus();
  };

  React.useEffect(() => {
    // Check if tosData is defined and has the required structure
    if (
      !tosData ||
      !tosData.title ||
      !tosData.sections ||
      !tosData.lastUpdated
    ) {
      throw new Error(
        'Terms of Service data is missing or invalid. Application cannot proceed without valid Terms of Service.',
      );
    }
  }, []);

  // No scroll tracking needed

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="terms-of-service-dialog-title"
      scroll="paper"
      fullWidth
      maxWidth="md"
      classes={{
        paper: classes.tosModalPaper,
      }}
      PaperProps={{
        tabIndex: -1,
        ref: paperRef,
        // Not a tab stop; hide the focus ring from handleEntered.
        style: { outline: 'none' },
      }}
      TransitionProps={{
        onEntered: handleEntered,
      }}
    >
      <Grid
        container
        className={classes.tosHeader}
      >
        <Grid
          item
          xs={3}
        >
          {/* Left column - can be used for a back button or left empty */}
        </Grid>
        <Grid
          item
          xs={6}
          container
          justifyContent="center"
          alignItems="center"
        >
          <Typography
            variant="h6"
            className={classes.tosTitle}
            id="terms-of-service-dialog-title"
          >
            TERMS OF SERVICE
          </Typography>
        </Grid>
        <Grid
          item
          xs={3}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <IconButton
            aria-label="Close terms of service dialog"
            className={classes.tosCloseButton}
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      <DialogContent
        dividers
        className={classes.tosContent}
      >
        {tosData.sections.map((section, index) => (
          <div
            key={index}
            className={classes.tosSection}
          >
            <Typography
              variant="subtitle1"
              className={classes.tosSectionTitle}
              gutterBottom
            >
              {section.title}
            </Typography>
            <Typography
              variant="body2"
              paragraph
              className={classes.tosSectionContent}
            >
              {section.content}
            </Typography>
          </div>
        ))}
        <Typography
          variant="caption"
          className={classes.tosLastUpdated}
        >
          Last updated: {tosData.lastUpdated}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.tosDialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          className={classes.tosAcceptButton}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TermsOfServiceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

// Using styles from theme/styles/termsOfService.js
export default withStyles((theme) => theme.termsOfService)(TermsOfServiceModal);
